import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";
import {
    type DirectionalBiasMode,
    generateAbstractSymbol,
    isValidGeneration,
    type SymbolGrammarMode,
    type SymbolPathMode,
    type SymbolGenerationResult,
    type SymbolShapeType,
    type SymbolStyleProfile,
} from "../../utils/symbolGenerator";
import type { SymbolSymmetry } from "../../utils/symbolGrid";

interface SymbolGeneratorState {
    width: number;
    height: number;
    rows: number;
    cols: number;
    density: number;
    complexity: number;
    branching: number;
    mergeBias: number;
    directionalBiasMode: DirectionalBiasMode;
    directionalBiasStrength: number;
    maxPathLength: number;
    pathGapCells: number;
    seed: string;
    symmetry: SymbolSymmetry;
    styleProfile: SymbolStyleProfile;
    grammarMode: SymbolGrammarMode;
    dotDensity: number;
    shapeFrequency: number;
    shapeType: SymbolShapeType;
    pathMode: SymbolPathMode;
    gridDisplacementStrength: number;
    cornerStyle: "sharp" | "rounded" | "bevel";
    cornerAmount: number;
    curvature: number;
    strokeWidth: number;
    strokeColor: string;
    backgroundColor: string;
    exportBackground: boolean;
    showGridOverlay: boolean;
    liveGeneration: boolean;
    warning: string | null;
    result: SymbolGenerationResult | null;
    setWidth: (v: number) => void;
    setHeight: (v: number) => void;
    setRows: (v: number) => void;
    setCols: (v: number) => void;
    setDensity: (v: number) => void;
    setComplexity: (v: number) => void;
    setBranching: (v: number) => void;
    setMergeBias: (v: number) => void;
    setDirectionalBiasMode: (v: DirectionalBiasMode) => void;
    setDirectionalBiasStrength: (v: number) => void;
    setMaxPathLength: (v: number) => void;
    setPathGapCells: (v: number) => void;
    setSeed: (v: string) => void;
    setSymmetry: (v: SymbolSymmetry) => void;
    setStyleProfile: (v: SymbolStyleProfile) => void;
    setGrammarMode: (v: SymbolGrammarMode) => void;
    setDotDensity: (v: number) => void;
    setShapeFrequency: (v: number) => void;
    setShapeType: (v: SymbolShapeType) => void;
    setPathMode: (v: SymbolPathMode) => void;
    setGridDisplacementStrength: (v: number) => void;
    setCornerStyle: (v: "sharp" | "rounded" | "bevel") => void;
    setCornerAmount: (v: number) => void;
    setCurvature: (v: number) => void;
    setStrokeWidth: (v: number) => void;
    setStrokeColor: (v: string) => void;
    setBackgroundColor: (v: string) => void;
    setExportBackground: (v: boolean) => void;
    setShowGridOverlay: (v: boolean) => void;
    setLiveGeneration: (v: boolean) => void;
    generate: () => void;
    reseed: () => void;
    randomizeParameters: () => void;
    buildExportSvgString: () => string;
    handleDownload: () => void;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
}

const SymbolGeneratorContext = createContext<SymbolGeneratorState | null>(null);

const DEFAULT_SYMBOL_GENERATOR_PRESET: Record<string, unknown> = {
    width: 640,
    height: 640,
    rows: 10,
    cols: 10,
    density: 28,
    complexity: 0.5,
    branching: 0.3,
    mergeBias: 0.35,
    directionalBiasMode: "none",
    directionalBiasStrength: 0.4,
    maxPathLength: 18,
    pathGapCells: 1,
    seed: "symbol-1",
    symmetry: "vertical",
    styleProfile: "neutral",
    grammarMode: "light",
    dotDensity: 0.35,
    shapeFrequency: 0.6,
    shapeType: "mixed",
    pathMode: "single",
    gridDisplacementStrength: 1,
    cornerStyle: "rounded",
    cornerAmount: 0.5,
    curvature: 0.55,
    strokeWidth: 10,
    strokeColor: "#111111",
    backgroundColor: "#ffffff",
    exportBackground: false,
    showGridOverlay: false,
    liveGeneration: false,
};

export function useSymbolGenerator() {
    const ctx = useContext(SymbolGeneratorContext);
    if (!ctx) {
        throw new Error("useSymbolGenerator must be used within SymbolGeneratorProvider");
    }
    return ctx;
}

export function SymbolGeneratorProvider({ children }: { children: React.ReactNode }) {
    const [width, setWidth] = useState(640);
    const [height, setHeight] = useState(640);
    const [rows, setRows] = useState(10);
    const [cols, setCols] = useState(10);
    const [density, setDensity] = useState(28);
    const [complexity, setComplexity] = useState(0.5);
    const [branching, setBranching] = useState(0.3);
    const [mergeBias, setMergeBias] = useState(0.35);
    const [directionalBiasMode, setDirectionalBiasMode] =
        useState<DirectionalBiasMode>("none");
    const [directionalBiasStrength, setDirectionalBiasStrength] = useState(0.4);
    const [maxPathLength, setMaxPathLength] = useState(18);
    const [pathGapCells, setPathGapCells] = useState(1);
    const [seed, setSeed] = useState("symbol-1");
    const [symmetry, setSymmetry] = useState<SymbolSymmetry>("vertical");
    const [styleProfile, setStyleProfile] = useState<SymbolStyleProfile>("neutral");
    const [grammarMode, setGrammarMode] = useState<SymbolGrammarMode>("light");
    const [dotDensity, setDotDensity] = useState(0.35);
    const [shapeFrequency, setShapeFrequency] = useState(0.6);
    const [shapeType, setShapeType] = useState<SymbolShapeType>("mixed");
    const [pathMode, setPathMode] = useState<SymbolPathMode>("single");
    const [gridDisplacementStrength, setGridDisplacementStrength] = useState(1);
    const [cornerStyle, setCornerStyle] = useState<"sharp" | "rounded" | "bevel">("rounded");
    const [cornerAmount, setCornerAmount] = useState(0.5);
    const [curvature, setCurvature] = useState(0.55);
    const [strokeWidth, setStrokeWidth] = useState(10);
    const [strokeColor, setStrokeColor] = useState("#111111");
    const [backgroundColor, setBackgroundColor] = useState("#ffffff");
    const [exportBackground, setExportBackground] = useState(false);
    const [showGridOverlay, setShowGridOverlay] = useState(false);
    const [liveGeneration, setLiveGeneration] = useState(false);
    const [result, setResult] = useState<SymbolGenerationResult | null>(null);
    const [warning, setWarning] = useState<string | null>(null);
    const [recentHashes, setRecentHashes] = useState<string[]>([]);

    const generateFromSeed = useCallback((sourceSeed: string) => {
        const built = generateAbstractSymbol({
            width,
            height,
            rows,
            cols,
            density,
            complexity,
            branching,
            mergeBias,
            directionalBiasMode,
            directionalBiasStrength,
            maxPathLength,
            pathGapCells,
            seed: sourceSeed,
            symmetry,
            styleProfile,
            grammarMode,
            dotDensity,
            shapeFrequency,
            shapeType,
            pathMode,
            gridDisplacementStrength,
            cornerStyle,
            cornerAmount,
            curvature,
            strokeWidth,
        });
        if (!isValidGeneration(built)) {
            setWarning("Generation rejected (too sparse/dense). Try reseed or adjust density.");
        } else {
            setWarning(null);
        }
        setResult(built);
        setRecentHashes((prev) => [built.hash, ...prev].slice(0, 16));
    }, [
        width,
        height,
        rows,
        cols,
        density,
        complexity,
        branching,
        mergeBias,
        directionalBiasMode,
        directionalBiasStrength,
        maxPathLength,
        pathGapCells,
        symmetry,
        styleProfile,
        grammarMode,
        dotDensity,
        shapeFrequency,
        shapeType,
        pathMode,
        gridDisplacementStrength,
        cornerStyle,
        cornerAmount,
        curvature,
        strokeWidth,
    ]);

    const generate = useCallback(() => {
        generateFromSeed(seed.trim() || "symbol-1");
    }, [generateFromSeed, seed]);

    const randomizeParameters = useCallback(() => {
        const nextWidth = Math.round((400 + Math.random() * 700) / 20) * 20;
        const nextHeight = Math.round((400 + Math.random() * 700) / 20) * 20;
        const nextRows = Math.max(4, Math.min(24, Math.round(8 + Math.random() * 12)));
        const nextCols = Math.max(4, Math.min(24, Math.round(8 + Math.random() * 12)));
        const nextDensity = Math.round((14 + Math.random() * 52) * 10) / 10;
        const nextComplexity = Math.round(Math.random() * 100) / 100;
        const nextBranching = Math.round(Math.random() * 100) / 100;
        const nextMergeBias = Math.round(Math.random() * 100) / 100;
        const directionalModes: DirectionalBiasMode[] = [
            "none",
            "horizontal",
            "vertical",
            "radial",
            "swirl",
        ];
        const nextDirectionalBiasMode =
            directionalModes[
                Math.floor(Math.random() * directionalModes.length)
            ] as DirectionalBiasMode;
        const nextDirectionalBiasStrength = Math.round(Math.random() * 100) / 100;
        const symmetries: SymbolSymmetry[] = ["none", "vertical", "horizontal"];
        const styleProfiles: SymbolStyleProfile[] = ["neutral", "bengali-like", "cjk-like"];
        const grammarModes: SymbolGrammarMode[] = ["off", "light", "rich"];
        const pathModes: SymbolPathMode[] = ["single", "multi"];
        const shapeTypes: SymbolShapeType[] = ["mixed", "circle", "square"];
        const cornerStyles: Array<"sharp" | "rounded" | "bevel"> = ["sharp", "rounded", "bevel"];
        const nextSymmetry = symmetries[Math.floor(Math.random() * symmetries.length)] as SymbolSymmetry;
        const nextStyle = styleProfiles[Math.floor(Math.random() * styleProfiles.length)] as SymbolStyleProfile;
        const nextGrammar = grammarModes[Math.floor(Math.random() * grammarModes.length)] as SymbolGrammarMode;
        const nextPathMode = pathModes[Math.floor(Math.random() * pathModes.length)] as SymbolPathMode;
        const nextDotDensity = Math.round(Math.random() * 100) / 100;
        const nextShapeFrequency = Math.round(Math.random() * 100) / 100;
        const nextShapeType = shapeTypes[Math.floor(Math.random() * shapeTypes.length)] as SymbolShapeType;
        const nextCornerStyle = cornerStyles[Math.floor(Math.random() * cornerStyles.length)] as "sharp" | "rounded" | "bevel";
        const nextCornerAmount = Math.round(Math.random() * 100) / 100;
        const nextCurvature = Math.round(Math.random() * 100) / 100;
        const nextStrokeWidth = Math.max(1, Math.round((2 + Math.random() * 22) * 10) / 10);
        const nextGridDisplacementStrength = Math.round(Math.random() * 100) / 100;
        const nextSeed = `symbol-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

        setWidth(nextWidth);
        setHeight(nextHeight);
        setRows(nextRows);
        setCols(nextCols);
        setDensity(nextDensity);
        setComplexity(nextComplexity);
        setBranching(nextBranching);
        setMergeBias(nextMergeBias);
        setDirectionalBiasMode(nextDirectionalBiasMode);
        setDirectionalBiasStrength(nextDirectionalBiasStrength);
        setSymmetry(nextSymmetry);
        setStyleProfile(nextStyle);
        setGrammarMode(nextGrammar);
        setPathMode(nextPathMode);
        setDotDensity(nextDotDensity);
        setShapeFrequency(nextShapeFrequency);
        setShapeType(nextShapeType);
        setGridDisplacementStrength(nextGridDisplacementStrength);
        setCornerStyle(nextCornerStyle);
        setCornerAmount(nextCornerAmount);
        setCurvature(nextCurvature);
        setStrokeWidth(nextStrokeWidth);
        setSeed(nextSeed);

        const built = generateAbstractSymbol({
            width: nextWidth,
            height: nextHeight,
            rows: nextRows,
            cols: nextCols,
            density: nextDensity,
            complexity: nextComplexity,
            branching: nextBranching,
            mergeBias: nextMergeBias,
            directionalBiasMode: nextDirectionalBiasMode,
            directionalBiasStrength: nextDirectionalBiasStrength,
            maxPathLength: Math.round(8 + Math.random() * 36),
            pathGapCells: Math.round(Math.random() * 2),
            seed: nextSeed,
            symmetry: nextSymmetry,
            styleProfile: nextStyle,
            grammarMode: nextGrammar,
            dotDensity: nextDotDensity,
            shapeFrequency: nextShapeFrequency,
            shapeType: nextShapeType,
            pathMode: nextPathMode,
            gridDisplacementStrength: nextGridDisplacementStrength,
            cornerStyle: nextCornerStyle,
            cornerAmount: nextCornerAmount,
            curvature: nextCurvature,
            strokeWidth: nextStrokeWidth,
        });
        setWarning(isValidGeneration(built) ? null : "Generated symbol may be too sparse/dense.");
        setResult(built);
        setRecentHashes((prev) => [built.hash, ...prev].slice(0, 16));
    }, []);

    const reseed = useCallback(() => {
        for (let i = 0; i < 8; i += 1) {
            const nextSeed = `symbol-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}-${i}`;
            const candidate = generateAbstractSymbol({
                width,
                height,
                rows,
                cols,
                density,
                complexity,
                branching,
                mergeBias,
                directionalBiasMode,
                directionalBiasStrength,
                maxPathLength,
                pathGapCells,
                seed: nextSeed,
                symmetry,
                styleProfile,
                grammarMode,
                dotDensity,
                shapeFrequency,
                shapeType,
                pathMode,
                gridDisplacementStrength,
                cornerStyle,
                cornerAmount,
                curvature,
                strokeWidth,
            });
            if (!recentHashes.includes(candidate.hash)) {
                setSeed(nextSeed);
                setResult(candidate);
                setRecentHashes((prev) => [candidate.hash, ...prev].slice(0, 16));
                setWarning(isValidGeneration(candidate) ? null : "Generated symbol may be too sparse/dense.");
                return;
            }
        }
        generateFromSeed(seed.trim() || "symbol-1");
    }, [
        width,
        height,
        rows,
        cols,
        density,
        complexity,
        branching,
        mergeBias,
        directionalBiasMode,
        directionalBiasStrength,
        maxPathLength,
        pathGapCells,
        seed,
        symmetry,
        styleProfile,
        grammarMode,
        dotDensity,
        shapeFrequency,
        shapeType,
        pathMode,
        gridDisplacementStrength,
        cornerStyle,
        cornerAmount,
        curvature,
        strokeWidth,
        recentHashes,
        generateFromSeed,
    ]);

    const buildExportSvgString = useCallback(() => {
        const pathDs = result?.pathDs ?? [];
        const dotPoints = result?.dotPoints ?? [];
        const dotRadius = Math.max(1, strokeWidth * 0.35);
        const lineJoin =
            cornerStyle === "bevel"
                ? "bevel"
                : cornerStyle === "sharp"
                  ? "miter"
                  : "round";
        const pathMarkup = pathDs
            .map(
                (pathD) =>
                    `  <path d="${pathD}" fill="none" stroke="${strokeColor}" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="${lineJoin}" />`,
            )
            .join("\n");
        const dotMarkup = dotPoints
            .map(
                (p) =>
                    `  <circle cx="${p.x.toFixed(2)}" cy="${p.y.toFixed(2)}" r="${dotRadius.toFixed(2)}" fill="${strokeColor}" />`,
            )
            .join("\n");
        const backgroundMarkup = exportBackground
            ? `  <rect width="${width}" height="${height}" fill="${backgroundColor}" />\n`
            : "";
        return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}">
${backgroundMarkup}
${pathMarkup}
${dotMarkup}
</svg>`;
    }, [backgroundColor, cornerStyle, exportBackground, height, result?.dotPoints, result?.pathDs, strokeColor, strokeWidth, width]);

    const handleDownload = useCallback(() => {
        if (!(result?.pathDs?.length || result?.dotPoints?.length)) return;
        const blob = new Blob([buildExportSvgString()], {
            type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `symbol-${seed.replace(/[^\w.-]+/g, "_")}-${Date.now()}.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [buildExportSvgString, result?.dotPoints, result?.pathDs, seed]);

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            width,
            height,
            rows,
            cols,
            density,
            complexity,
            branching,
            mergeBias,
            directionalBiasMode,
            directionalBiasStrength,
            maxPathLength,
            pathGapCells,
            seed,
            symmetry,
            styleProfile,
            grammarMode,
            dotDensity,
            shapeFrequency,
            shapeType,
            pathMode,
            gridDisplacementStrength,
            cornerStyle,
            cornerAmount,
            curvature,
            strokeWidth,
            strokeColor,
            backgroundColor,
            exportBackground,
            showGridOverlay,
            liveGeneration,
        }),
        [
            width,
            height,
            rows,
            cols,
            density,
            complexity,
            branching,
            mergeBias,
            directionalBiasMode,
            directionalBiasStrength,
            maxPathLength,
            pathGapCells,
            seed,
            symmetry,
            styleProfile,
            grammarMode,
            dotDensity,
            shapeFrequency,
            shapeType,
            pathMode,
            gridDisplacementStrength,
            cornerStyle,
            cornerAmount,
            curvature,
            strokeWidth,
            strokeColor,
            backgroundColor,
            exportBackground,
            showGridOverlay,
            liveGeneration,
        ],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (values.width != null) setWidth(values.width as number);
        if (values.height != null) setHeight(values.height as number);
        if (values.rows != null) setRows(values.rows as number);
        if (values.cols != null) setCols(values.cols as number);
        if (values.density != null) setDensity(values.density as number);
        if (values.complexity != null) setComplexity(values.complexity as number);
        if (values.branching != null) setBranching(values.branching as number);
        if (values.mergeBias != null) setMergeBias(values.mergeBias as number);
        if (
            values.directionalBiasMode === "none" ||
            values.directionalBiasMode === "horizontal" ||
            values.directionalBiasMode === "vertical" ||
            values.directionalBiasMode === "radial" ||
            values.directionalBiasMode === "swirl"
        ) {
            setDirectionalBiasMode(values.directionalBiasMode);
        }
        if (values.directionalBiasStrength != null) {
            setDirectionalBiasStrength(values.directionalBiasStrength as number);
        }
        if (values.maxPathLength != null) {
            setMaxPathLength(values.maxPathLength as number);
        }
        if (values.pathGapCells != null) {
            setPathGapCells(values.pathGapCells as number);
        }
        if (values.seed != null) setSeed(values.seed as string);
        if (values.symmetry === "none" || values.symmetry === "vertical" || values.symmetry === "horizontal") {
            setSymmetry(values.symmetry);
        }
        if (values.styleProfile === "neutral" || values.styleProfile === "bengali-like" || values.styleProfile === "cjk-like") {
            setStyleProfile(values.styleProfile);
        }
        if (values.grammarMode === "off" || values.grammarMode === "light" || values.grammarMode === "rich") {
            setGrammarMode(values.grammarMode);
        }
        if (values.dotDensity != null) setDotDensity(values.dotDensity as number);
        if (values.shapeFrequency != null) setShapeFrequency(values.shapeFrequency as number);
        if (values.shapeType === "mixed" || values.shapeType === "circle" || values.shapeType === "square") {
            setShapeType(values.shapeType);
        }
        if (values.pathMode === "single" || values.pathMode === "multi") {
            setPathMode(values.pathMode);
        }
        if (values.gridDisplacementStrength != null) {
            setGridDisplacementStrength(values.gridDisplacementStrength as number);
        }
        if (
            values.cornerStyle === "sharp" ||
            values.cornerStyle === "rounded" ||
            values.cornerStyle === "bevel"
        ) {
            setCornerStyle(values.cornerStyle);
        }
        if (values.cornerAmount != null) setCornerAmount(values.cornerAmount as number);
        if (values.curvature != null) setCurvature(values.curvature as number);
        if (values.strokeWidth != null) setStrokeWidth(values.strokeWidth as number);
        if (values.strokeColor != null) setStrokeColor(values.strokeColor as string);
        if (values.backgroundColor != null) setBackgroundColor(values.backgroundColor as string);
        if (values.exportBackground != null) setExportBackground(values.exportBackground as boolean);
        if (values.showGridOverlay != null) setShowGridOverlay(values.showGridOverlay as boolean);
        if (values.liveGeneration != null) setLiveGeneration(values.liveGeneration as boolean);
    }, []);

    const { resetParameters } = useToolParamPersistence({
        toolId: "symbol-generator",
        defaultPreset: DEFAULT_SYMBOL_GENERATOR_PRESET,
        getPresetValues,
        applyPreset,
    });

    useEffect(() => {
        if (!result) generate();
    }, [result, generate]);

    useEffect(() => {
        if (!liveGeneration) return;
        generateFromSeed(seed.trim() || "symbol-1");
    }, [liveGeneration, seed, generateFromSeed]);

    const value = useMemo(
        (): SymbolGeneratorState => ({
            width,
            height,
            rows,
            cols,
            density,
            complexity,
            branching,
            mergeBias,
            directionalBiasMode,
            directionalBiasStrength,
            maxPathLength,
            pathGapCells,
            seed,
            symmetry,
            styleProfile,
            grammarMode,
            dotDensity,
            shapeFrequency,
            shapeType,
            pathMode,
            gridDisplacementStrength,
            cornerStyle,
            cornerAmount,
            curvature,
            strokeWidth,
            strokeColor,
            backgroundColor,
            exportBackground,
            showGridOverlay,
            liveGeneration,
            warning,
            result,
            setWidth,
            setHeight,
            setRows,
            setCols,
            setDensity,
            setComplexity,
            setBranching,
            setMergeBias,
            setDirectionalBiasMode,
            setDirectionalBiasStrength,
            setMaxPathLength,
            setPathGapCells,
            setSeed,
            setSymmetry,
            setStyleProfile,
            setGrammarMode,
            setDotDensity,
            setShapeFrequency,
            setShapeType,
            setPathMode,
            setGridDisplacementStrength,
            setCornerStyle,
            setCornerAmount,
            setCurvature,
            setStrokeWidth,
            setStrokeColor,
            setBackgroundColor,
            setExportBackground,
            setShowGridOverlay,
            setLiveGeneration,
            generate,
            reseed,
            randomizeParameters,
            buildExportSvgString,
            handleDownload,
            applyPreset,
            getPresetValues,
            resetParameters,
        }),
        [
            width,
            height,
            rows,
            cols,
            density,
            complexity,
            branching,
            mergeBias,
            directionalBiasMode,
            directionalBiasStrength,
            maxPathLength,
            pathGapCells,
            seed,
            symmetry,
            styleProfile,
            grammarMode,
            dotDensity,
            shapeFrequency,
            shapeType,
            pathMode,
            gridDisplacementStrength,
            cornerStyle,
            cornerAmount,
            curvature,
            strokeWidth,
            strokeColor,
            backgroundColor,
            exportBackground,
            showGridOverlay,
            liveGeneration,
            warning,
            result,
            generate,
            reseed,
            randomizeParameters,
            buildExportSvgString,
            handleDownload,
            applyPreset,
            getPresetValues,
            resetParameters,
        ],
    );

    return (
        <SymbolGeneratorContext.Provider value={value}>
            {children}
        </SymbolGeneratorContext.Provider>
    );
}
